import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { ArrowLeft, ChevronLeft, ChevronRight, Truck, ShieldCheck, Feather, Droplets, Sparkles, Leaf, WashingMachine, Palette } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const BENEFITS = [
  { icon: ShieldCheck, label: "Anti-slip grip" },
  { icon: Droplets, label: "Sweat absorbent" },
  { icon: Feather, label: "Gentle on skin" },
  { icon: Sparkles, label: "Odour resistant" },
  { icon: WashingMachine, label: "Washer & dryer safe" },
  { icon: Leaf, label: "Microfibre waffle weave" },
  { icon: Palette, label: "Original artist print" },
  { icon: Truck, label: "Ships from Toronto" },
];

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then(r => {
      setProduct(r.data);
      setVariant(r.data.variants?.[0]?.label || null);
      setActiveImg(0);
    });
  }, [slug]);

  if (!product) return <div className="max-w-[1400px] mx-auto px-6 py-24 opacity-60">Loading…</div>;

  const onAdd = () => {
    add(product, variant, 1);
    toast.success(`'${product.name}' added to bag`);
  };

  const next = () => setActiveImg((i) => (i + 1) % product.images.length);
  const prev = () => setActiveImg((i) => (i - 1 + product.images.length) % product.images.length);

  const productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": `${product.name} — KaiyaYoga Towel`,
    "description": `Original monotype yoga towel by Toronto artist Kelsey-Lynn. Anti-slip microfibre waffle weave. Designed in Canada.`,
    "image": product.images,
    "sku": product.slug,
    "brand": { "@type": "Brand", "name": "KaiyaYoga" },
    "offers": {
      "@type": "Offer",
      "url": `https://kaiyayoga.com/product/${product.slug}`,
      "priceCurrency": "CAD",
      "price": product.price.toFixed(2),
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
    },
  };

  return (
    <div data-testid="product-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16">
      <Seo
        title={`'${product.name}' Yoga Towel`}
        description={`Original monotype yoga towel by Toronto artist Kelsey-Lynn. Anti-slip microfibre waffle. Free shipping on 2+. Designed in Canada.`}
        path={`/product/${product.slug}`}
        image={product.images?.[0]}
        type="product"
        jsonLd={productJsonLd}
      />
      <Link to="/products" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100 mb-8">
        <ArrowLeft size={14} /> All towels
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-24">
        {/* Gallery with carousel */}
        <div className="md:col-span-7 space-y-4">
          <div className="relative image-soft aspect-[3/4] md:aspect-[4/5] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img
              key={activeImg}
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-cover animate-in fade-in duration-500"
            />
            {product.images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  data-testid="carousel-prev"
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md hover:scale-110 transition-transform"
                  style={{ background: "rgba(249,246,240,0.85)", color: "var(--kaiya-text)" }}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={next}
                  data-testid="carousel-next"
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full backdrop-blur-md hover:scale-110 transition-transform"
                  style={{ background: "rgba(249,246,240,0.85)", color: "var(--kaiya-text)" }}
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md" style={{ background: "rgba(249,246,240,0.7)" }}>
                  {product.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`carousel-dot-${i}`}
                      className="w-2 h-2 rounded-full transition-all"
                      style={{
                        background: activeImg === i ? "var(--kaiya-primary)" : "var(--kaiya-border)",
                        transform: activeImg === i ? "scale(1.3)" : "scale(1)",
                      }}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className="image-soft aspect-square overflow-hidden border-2 transition-colors"
                  style={{
                    borderColor: activeImg === i ? "var(--kaiya-primary)" : "transparent",
                    background: "var(--kaiya-bg-2)",
                  }}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
          <div className="label-eyebrow mb-3">Inspired by</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-2">&apos;{product.name}&apos;</h1>
          <a href="https://www.instagram.com/kelseyltc" target="_blank" rel="noreferrer" className="text-sm underline underline-offset-4 inline-block mb-8" style={{ color: "var(--kaiya-primary)" }}>
            Monotype Print by @kelseyltc
          </a>
          <div className="font-serif-display text-3xl mb-8">${product.price.toFixed(2)} <span className="text-sm opacity-50">CAD</span></div>

          <div className="mb-8 text-base leading-relaxed opacity-90 space-y-4">
            <p>
              A microfibre waffle yoga towel featuring an original monotype print by Toronto artist <strong>Kelsey-Lynn</strong>. Anti-slip when wet, quick to dry, and soft on skin — designed to move with you through every practice.
            </p>
            <p>
              We hope her art inspires you to embrace creation and beauty, both on and off the mat.
            </p>
          </div>

          {product.variants?.length > 0 && (
            <div className="mb-8">
              <div className="label-eyebrow mb-3">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const active = variant === v.label;
                  return (
                    <button
                      key={v.label}
                      data-testid={`variant-${v.label}`}
                      onClick={() => setVariant(v.label)}
                      className="px-6 py-3 rounded-xl border text-sm transition-colors"
                      style={{
                        borderColor: active ? "var(--kaiya-text)" : "var(--kaiya-border)",
                        background: active ? "var(--kaiya-text)" : "transparent",
                        color: active ? "var(--kaiya-bg)" : "var(--kaiya-text)",
                      }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            data-testid="add-to-cart-btn"
            onClick={onAdd}
            className="btn-primary w-full"
          >
            Add to bag — ${product.price.toFixed(2)}
          </button>

          <div className="mt-6 text-xs text-center opacity-70 italic" style={{ color: "var(--kaiya-primary)" }}>
            A portion of your purchase directly supports the artist.
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <section className="mb-16">
        <div className="label-eyebrow mb-3">Why you&apos;ll love it</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mb-8">Designed to move with you.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {BENEFITS.map(({ icon: Icon, label }) => (
            <div key={label} className="kaiya-card p-6" data-testid={`benefit-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={22} style={{ color: "var(--kaiya-primary)" }} className="mb-3" />
              <div className="font-medium text-sm">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CARE + SPECS */}
      <section className="kaiya-card p-8 md:p-12 grid md:grid-cols-2 gap-10" style={{ background: "var(--kaiya-bg-2)" }}>
        <div>
          <div className="label-eyebrow mb-3">Materials</div>
          <ul className="space-y-2 text-sm">
            <li>Microfibre waffle weave</li>
            <li>80% polyester / 20% nylon</li>
            <li>Standard yoga towel dimensions</li>
          </ul>
        </div>
        <div>
          <div className="label-eyebrow mb-3">Care</div>
          <ul className="space-y-2 text-sm">
            <li>Machine wash cold, gentle cycle</li>
            <li>Tumble dry low</li>
            <li>No fabric softener</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
